import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Group,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { GAME_TYPES, VOLATILITIES, type Volatility } from '@seo/contracts';
import { RowEditor } from '../components/RowEditor';
import { useCan } from '../lib/session';
import { API_BASE, publicMessageOf, useTRPC } from '../lib/trpc';
import { zodResolver } from '../lib/zod-resolver';
import { EntityKeywords } from './EntityKeywords';
import classes from './EntityPage.module.css';

/**
 * Картка гри (`slotNew` у мокапі, §1.4) — 14.08 доведена до складу мокапа.
 *
 * До цього форма редагування підставляла лише 7 полів із ~20: ставки, лінії, чотири тумблери
 * механік і шість медіа-слотів існували в схемі й у `gameInput`, але не мали жодного поля на
 * екрані. Оператор бачив назву, тип, провайдера й рік — решту доводилось редагувати напряму в
 * базі. `multiplayer` — пʼятий тумблер, якого немає в мокапі, але є в таблиці `games`: мокап
 * тут — прототип, а не стеля вимог.
 */

/** `NumberInput` віддає `number | ''` — порожнє поле це рядок, а не `null`. */
function optionalNumber(value: number | string): number | null {
  return typeof value === 'number' ? value : null;
}

const TYPE_LABELS: Record<string, string> = {
  slot: 'слот',
  crash: 'crash',
  live: 'live',
  arcade: 'аркада',
  table: 'настільна',
};

const VOLATILITY_LABELS: Record<Volatility, string> = {
  low: 'низька',
  medium: 'середня',
  high: 'висока',
  very_high: 'дуже висока',
};

const parseVolatility = (value: string | null | undefined): '' | Volatility =>
  VOLATILITIES.find((v) => v === value) ?? '';

/** Форма — єдине місце, де RTP існує відсотком; дріт і колонка тримають базисні пункти. */
const RTP_BPS_PER_PERCENT = 100;
const RTP_MIN_PERCENT = 0;
const RTP_MAX_PERCENT = 100;

/** Ставки в базі — мінорні одиниці (§1.4, EUR за умовчанням); форма показує їх сумою. */
const MINOR_PER_MAJOR = 100;

const rtpBpsToPercentInput = (bps: number | null | undefined): string =>
  bps == null ? '' : (bps / RTP_BPS_PER_PERCENT).toFixed(2);

const minorToMajorInput = (minor: number | null | undefined): string =>
  minor == null ? '' : (minor / MINOR_PER_MAJOR).toFixed(2);

/** Порожній рядок — «ще не задокументовано», а не 0: нульова ставка теж значення. */
const moneyField = z.string().superRefine((raw, ctx) => {
  const value = raw.trim();
  if (value === '') return;
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount < 0) {
    ctx.addIssue('Сума — невідʼємне число, напр. 0.20');
  }
});

const gameEditSchema = z
  .object({
    name: z.string().trim().min(1, 'Вкажіть назву гри').max(200),
    type: z.enum(GAME_TYPES),
    providerId: z.string().min(1, 'Оберіть провайдера'),
    releaseYear: z.union([z.number(), z.literal('')]),
    demoIframeUrl: z.string(),
    rtp: z.string().superRefine((raw, ctx) => {
      const value = raw.trim();
      if (value === '') return;
      const percent = Number(value);
      if (!Number.isFinite(percent)) {
        ctx.addIssue('RTP — число у відсотках, напр. 96.5');
        return;
      }
      if (percent < RTP_MIN_PERCENT || percent > RTP_MAX_PERCENT) {
        ctx.addIssue(`RTP — відсоток від ${String(RTP_MIN_PERCENT)} до ${String(RTP_MAX_PERCENT)}`);
      }
    }),
    volatility: z.literal('').or(z.enum(VOLATILITIES)),
    maxWinMultiplier: z.union([z.number(), z.literal('')]),
    minBet: moneyField,
    maxBet: moneyField,
    linesOrWays: z.string(),
    bonusRound: z.boolean(),
    freeSpins: z.boolean(),
    provablyFair: z.boolean(),
    autoCashout: z.boolean(),
    multiplayer: z.boolean(),
  })
  // Той самий інваріант, що й у сервісі (`validateBetRange`) — тут він ловить помилку там,
  // де оператор ще бачить, що набрав, а не за круговоротом до сервера й назад.
  .superRefine((values, ctx) => {
    const min = values.minBet.trim();
    const max = values.maxBet.trim();
    if (min !== '' && max !== '' && Number(min) > Number(max)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Максимальна ставка не може бути меншою за мінімальну.',
        path: ['maxBet'],
      });
    }
  });

type GameEditValues = z.infer<typeof gameEditSchema>;

export interface EditableGameReadiness {
  coreReady: boolean;
  mediaReady: boolean;
  missingCore: string[];
  missingMedia: string[];
}

export interface EditableGameAsset {
  slot: string;
  mediaFileId: string | null;
  fileKey: string | null;
}

export interface EditableGame {
  id: string;
  name: string;
  type: string;
  providerId: string;
  releaseYear: number | null;
  demoIframeUrl?: string | null;
  demoHttpStatus?: number | null;
  rtpBps?: number | null;
  volatility?: string | null;
  maxWinMultiplier?: number | null;
  minBet?: number | null;
  maxBet?: number | null;
  linesOrWays?: string | null;
  bonusRound?: boolean;
  freeSpins?: boolean;
  provablyFair?: boolean;
  autoCashout?: boolean;
  multiplayer?: boolean;
  readiness?: EditableGameReadiness;
  assets?: EditableGameAsset[];
}

type GameAssetColumn =
  | 'thumbnail16x9Id'
  | 'banner1280x420Id'
  | 'mobileScreenshotId'
  | 'screen1Id'
  | 'screen2Id'
  | 'screen3Id';

interface GameAssetSlotDef {
  column: GameAssetColumn;
  slot: string;
  label: string;
  spec: string;
}

/**
 * Шість слотів мокапа (рядок 4705): порядок і назви — ті самі, що в `ASSET_SLOTS`
 * (`packages/core/src/services/games.ts`), бо саме за `slot` картка гри зіставляє відповідь
 * `getGame` із рядком тут.
 */
const GAME_ASSET_SLOTS: GameAssetSlotDef[] = [
  { column: 'thumbnail16x9Id', slot: 'thumbnail_16x9', label: 'Мініатюра 16:9', spec: 'JPG/PNG/WebP, 16:9' },
  { column: 'banner1280x420Id', slot: 'banner_1280x420', label: 'Банер 1280×420', spec: 'JPG/PNG/WebP 1280×420' },
  { column: 'mobileScreenshotId', slot: 'mobile_screenshot', label: 'Мобільний скрін', spec: 'PNG, вертикальний' },
  { column: 'screen1Id', slot: 'screen_1', label: 'Скрін 1', spec: 'JPG/PNG/WebP' },
  { column: 'screen2Id', slot: 'screen_2', label: 'Скрін 2', spec: 'JPG/PNG/WebP' },
  { column: 'screen3Id', slot: 'screen_3', label: 'Скрін 3', spec: 'JPG/PNG/WebP' },
];

/** Літеральний ключ на кожній гілці — інакше обчислюваний `[column]:` розширюється до
 * загального індексу й перестає збігатись зі строгою формою патча `games.update`. */
function assetPatch(column: GameAssetColumn, mediaFileId: string | null): Record<GameAssetColumn, string | null> {
  return {
    thumbnail16x9Id: null,
    banner1280x420Id: null,
    mobileScreenshotId: null,
    screen1Id: null,
    screen2Id: null,
    screen3Id: null,
    [column]: mediaFileId,
  };
}

interface UploadResult {
  mediaFileId?: string;
  message?: string;
  error?: string;
}

/**
 * Медіа гри — той самий поділ на два кроки, що й у слотах бренду (`AssetSlots.tsx`): байти
 * йдуть у `POST /media/upload`, який єдиний їх санітизує, а в колонку `games` кладеться лише
 * повернутий `mediaFileId`. Тут немає окремої процедури `asset` (на відміну від брендів) —
 * слот гри є колонкою самої таблиці, тож привʼязка йде звичайним `games.update`.
 */
function GameMediaSlots({
  gameId,
  assets,
}: {
  gameId: string;
  assets: EditableGameAsset[] | undefined;
}): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const canWrite = useCan('databases', 'write');
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});
  const [busySlot, setBusySlot] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const attach = useMutation(
    trpc.games.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.games.pathKey() });
      },
    }),
  );

  const onFile = async (def: GameAssetSlotDef, file: File): Promise<void> => {
    setBusySlot(def.slot);
    setUploadError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      // Слот їде разом із байтами — за ним завантажувач звіряє допустимі формати й розмір.
      body.append('slot', def.slot);
      const res = await fetch(`${API_BASE}/media/upload`, {
        method: 'POST',
        body,
        credentials: 'include',
      });
      const json = (await res.json()) as UploadResult;
      if (!res.ok || json.mediaFileId === undefined) {
        setUploadError(json.message ?? json.error ?? 'Не вдалося завантажити файл.');
        return;
      }
      attach.mutate({ id: gameId, patch: assetPatch(def.column, json.mediaFileId) });
    } catch (err) {
      setUploadError(publicMessageOf(err));
    } finally {
      setBusySlot(null);
    }
  };

  const bySlot = new Map((assets ?? []).map((row) => [row.slot, row]));

  return (
    <Stack gap={6} data-testid="game-media-slots">
      <Text className={classes.dim}>
        Підставляються у шаблон монослота; порожній слот — робочий стан, а не помилка
      </Text>
      {GAME_ASSET_SLOTS.map((def) => {
        const row = bySlot.get(def.slot);
        const present = row?.mediaFileId != null;
        return (
          <Group key={def.slot} gap={8} data-testid={`edit-game-asset-${def.slot}`}>
            <Text size="sm" style={{ width: 150 }}>
              {def.label}
            </Text>
            <Badge
              size="xs"
              color={present ? 'mint' : 'slate'}
              data-ready={present}
              data-testid={`edit-game-asset-state-${def.slot}`}
            >
              {present ? 'є' : '✗ немає'}
            </Badge>
            <Text className={classes.dim} style={{ flex: 1, minWidth: 0 }}>
              {def.spec}
            </Text>
            {canWrite ? (
              <>
                <input
                  ref={(element) => {
                    inputs.current[def.slot] = element;
                  }}
                  type="file"
                  accept="image/*"
                  hidden
                  data-testid={`edit-game-asset-file-${def.slot}`}
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0];
                    if (file) void onFile(def, file);
                    // Скидаємо, щоб повторний вибір того самого файлу знову дав `change`.
                    event.currentTarget.value = '';
                  }}
                />
                <Button
                  type="button"
                  size="compact-xs"
                  variant="default"
                  loading={busySlot === def.slot}
                  data-testid={`edit-game-asset-upload-${def.slot}`}
                  onClick={() => {
                    inputs.current[def.slot]?.click();
                  }}
                >
                  {present ? 'Замінити' : 'Завантажити'}
                </Button>
                {present ? (
                  // Звільнити слот — не видалити файл: на нього може посилатися вже
                  // опублікована сторінка, тож `media_files` лишається.
                  <Button
                    type="button"
                    size="compact-xs"
                    variant="subtle"
                    data-testid={`edit-game-asset-clear-${def.slot}`}
                    onClick={() => {
                      attach.mutate({ id: gameId, patch: assetPatch(def.column, null) });
                    }}
                  >
                    звільнити
                  </Button>
                ) : null}
              </>
            ) : null}
          </Group>
        );
      })}
      {uploadError === null && attach.error === null ? null : (
        <Text className={classes.danger} data-testid="edit-game-asset-error" role="alert">
          {uploadError ?? publicMessageOf(attach.error)}
        </Text>
      )}
    </Stack>
  );
}

/**
 * `games.readiness` перераховує й ЗБЕРІГАЄ обидва прапорці (на відміну від `readiness`,
 * вбудованої у відповідь `games.byId`, яка лише читає те, що вже лежить у рядку) — і не мала
 * викликача: оператор міг побачити «не готова», але не міг попросити перевірити ще раз, не
 * перевідкриваючи картку.
 */
function GameReadinessCheck({
  gameId,
  fallback,
}: {
  gameId: string;
  fallback: EditableGameReadiness | undefined;
}): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const check = useQuery({ ...trpc.games.readiness.queryOptions({ id: gameId }), enabled: false });

  const readiness = check.data ?? fallback;

  return (
    <Group gap={8} align="center" data-testid="edit-game-readiness">
      <Button
        type="button"
        size="compact-xs"
        variant="default"
        loading={check.isFetching}
        data-testid="edit-game-readiness-refresh"
        onClick={() => {
          void check.refetch().then(async () => {
            await queryClient.invalidateQueries({ queryKey: trpc.games.pathKey() });
          });
        }}
      >
        Перевірити готовність
      </Button>
      {readiness === undefined ? (
        <Text className={classes.dim} data-testid="edit-game-readiness-unknown">
          не перевірено
        </Text>
      ) : (
        <>
          <Badge
            size="sm"
            color={readiness.coreReady ? 'mint' : 'rose'}
            data-ready={readiness.coreReady}
            data-testid="edit-game-readiness-core"
          >
            дані
          </Badge>
          <Badge
            size="sm"
            color={readiness.mediaReady ? 'mint' : 'rose'}
            data-ready={readiness.mediaReady}
            data-testid="edit-game-readiness-media"
          >
            медіа
          </Badge>
        </>
      )}
      {check.error === null ? null : (
        <Text className={classes.danger} data-testid="edit-game-readiness-error" role="alert">
          {publicMessageOf(check.error)}
        </Text>
      )}
    </Group>
  );
}

export function GameEditor({
  row,
  onClose,
}: {
  row: EditableGame;
  onClose: () => void;
}): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const providers = useQuery(
    trpc.reference.providers.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
  );

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.games.pathKey() });
  };
  const update = useMutation(trpc.games.update.mutationOptions({ onSuccess: refresh }));
  /** Жива перевірка демо — та сама кнопка, що в мокапі, і той самий порт, що в пінгів. */
  const checkDemo = useMutation(trpc.games.checkDemo.mutationOptions({ onSuccess: refresh }));
  const remove = useMutation(
    trpc.games.remove.mutationOptions({
      onSuccess: async () => {
        await refresh();
        onClose();
      },
    }),
  );

  const form = useForm<GameEditValues>({
    initialValues: {
      name: row.name,
      type: row.type as (typeof GAME_TYPES)[number],
      providerId: row.providerId,
      releaseYear: row.releaseYear ?? '',
      demoIframeUrl: row.demoIframeUrl ?? '',
      rtp: rtpBpsToPercentInput(row.rtpBps),
      volatility: parseVolatility(row.volatility),
      maxWinMultiplier: row.maxWinMultiplier ?? '',
      minBet: minorToMajorInput(row.minBet),
      maxBet: minorToMajorInput(row.maxBet),
      linesOrWays: row.linesOrWays ?? '',
      bonusRound: row.bonusRound ?? false,
      freeSpins: row.freeSpins ?? false,
      provablyFair: row.provablyFair ?? false,
      autoCashout: row.autoCashout ?? false,
      multiplayer: row.multiplayer ?? false,
    },
    validate: zodResolver(gameEditSchema),
  });

  const volatilityOptions = [
    { value: '', label: '—' },
    ...VOLATILITIES.map((v) => ({ value: v, label: VOLATILITY_LABELS[v] })),
  ];

  return (
    <RowEditor
      title={row.name}
      testId="game"
      zone="databases"
      onClose={onClose}
      busy={update.isPending}
      error={update.error}
      deleting={remove.isPending}
      deleteError={remove.error}
      confirmingDelete={confirming}
      onConfirmingDeleteChange={setConfirming}
      deletePrompt={`Видалити гру «${row.name}»? Її ключові слова й привʼязки до сайтів лишаться сиротами.`}
      onDelete={() => {
        remove.mutate({ id: row.id });
      }}
      onSubmit={form.onSubmit((values) => {
        const rtp = values.rtp.trim();
        const minBet = values.minBet.trim();
        const maxBet = values.maxBet.trim();
        const demoIframeUrl = values.demoIframeUrl.trim();
        const linesOrWays = values.linesOrWays.trim();
        update.mutate({
          id: row.id,
          patch: {
            name: values.name,
            type: values.type,
            providerId: values.providerId,
            releaseYear: optionalNumber(values.releaseYear),
            demoIframeUrl: demoIframeUrl === '' ? null : demoIframeUrl,
            // Оператор бачить відсоток; дріт і колонка тримають базисні пункти.
            rtpBps: rtp === '' ? null : Math.round(Number(rtp) * RTP_BPS_PER_PERCENT),
            volatility: values.volatility === '' ? null : values.volatility,
            maxWinMultiplier: optionalNumber(values.maxWinMultiplier),
            minBet: minBet === '' ? null : Math.round(Number(minBet) * MINOR_PER_MAJOR),
            maxBet: maxBet === '' ? null : Math.round(Number(maxBet) * MINOR_PER_MAJOR),
            linesOrWays: linesOrWays === '' ? null : linesOrWays,
            bonusRound: values.bonusRound,
            freeSpins: values.freeSpins,
            provablyFair: values.provablyFair,
            autoCashout: values.autoCashout,
            multiplayer: values.multiplayer,
          },
        });
      })}
    >
      <Stack gap="md" style={{ width: '100%' }}>
        <Box>
          <Text className={classes.dim} mb={6}>
            Загальна інформація
          </Text>
          <Group gap="sm" align="flex-start" wrap="wrap">
            <TextInput
              size="xs"
              label="Назва"
              data-testid="edit-game-name"
              style={{ width: 200 }}
              {...form.getInputProps('name')}
            />
            <Select
              size="xs"
              label="Тип"
              allowDeselect={false}
              data={GAME_TYPES.map((t) => ({ value: t, label: TYPE_LABELS[t] ?? t }))}
              data-testid="edit-game-type"
              style={{ width: 130 }}
              {...form.getInputProps('type')}
            />
            <Select
              size="xs"
              label="Провайдер"
              searchable
              allowDeselect={false}
              data={(providers.data?.items ?? []).map((p) => ({ value: p.id, label: p.name }))}
              data-testid="edit-game-provider"
              style={{ width: 180 }}
              {...form.getInputProps('providerId')}
            />
            <NumberInput
              size="xs"
              label="Рік"
              min={1990}
              max={2100}
              data-testid="edit-game-release"
              style={{ width: 100 }}
              {...form.getInputProps('releaseYear')}
            />
            <TextInput
              size="xs"
              label="Demo iframe URL"
              data-testid="edit-game-demo-url"
              style={{ width: 260 }}
              {...form.getInputProps('demoIframeUrl')}
            />
            {/*
              * «ПЕРЕВІРИТИ» з мокапа. Перевірка ходить у мережу тим самим портом, що й пінги
              * моніторингу — окремого шляху назовні тут не заводиться, — і **зберігає**
              * результат: доти `demo_http_status` писав лише сідер, тобто колонка показувала
              * стан, якого ніхто не міряв.
              */}
            <Text className={classes.dim} mt={22} data-testid="edit-game-demo-status">
              demo HTTP: {checkDemo.data?.status ?? row.demoHttpStatus ?? 'не перевірено'}
            </Text>
            <Button
              size="compact-xs"
              variant="default"
              mt={22}
              loading={checkDemo.isPending}
              disabled={(form.values.demoIframeUrl ?? '') === ''}
              data-testid="edit-game-demo-check"
              onClick={() => {
                checkDemo.mutate({ id: row.id });
              }}
            >
              Перевірити
            </Button>
            {checkDemo.error === null ? null : (
              <Text className={classes.danger} mt={22} data-testid="edit-game-demo-error" role="alert">
                {publicMessageOf(checkDemo.error)}
              </Text>
            )}
          </Group>
        </Box>

        <Box>
          <Text className={classes.dim} mb={6}>
            Математика та механіки
          </Text>
          <Group gap="sm" align="flex-start" wrap="wrap">
            <TextInput
              size="xs"
              label="RTP %"
              data-testid="edit-game-rtp"
              style={{ width: 90 }}
              {...form.getInputProps('rtp')}
            />
            <Select
              size="xs"
              label="Волатильність"
              data={volatilityOptions}
              value={form.values.volatility}
              onChange={(value) => {
                form.setFieldValue('volatility', parseVolatility(value));
              }}
              error={form.errors.volatility}
              data-testid="edit-game-volatility"
              style={{ width: 150 }}
            />
            <NumberInput
              size="xs"
              label="Max win (×)"
              min={0}
              data-testid="edit-game-max-win"
              style={{ width: 120 }}
              {...form.getInputProps('maxWinMultiplier')}
            />
            <TextInput
              size="xs"
              label="Min bet"
              data-testid="edit-game-min-bet"
              style={{ width: 90 }}
              {...form.getInputProps('minBet')}
            />
            <TextInput
              size="xs"
              label="Max bet"
              data-testid="edit-game-max-bet"
              style={{ width: 90 }}
              {...form.getInputProps('maxBet')}
            />
            <TextInput
              size="xs"
              label="Лінії / ways"
              data-testid="edit-game-lines"
              style={{ width: 160 }}
              {...form.getInputProps('linesOrWays')}
            />
          </Group>
          <Group gap="md" mt={10}>
            <Switch
              size="xs"
              label="bonus round"
              data-testid="edit-game-bonus-round"
              {...form.getInputProps('bonusRound', { type: 'checkbox' })}
            />
            <Switch
              size="xs"
              label="free spins"
              data-testid="edit-game-free-spins"
              {...form.getInputProps('freeSpins', { type: 'checkbox' })}
            />
            <Switch
              size="xs"
              label="provably fair"
              data-testid="edit-game-provably-fair"
              {...form.getInputProps('provablyFair', { type: 'checkbox' })}
            />
            <Switch
              size="xs"
              label="auto cashout"
              data-testid="edit-game-auto-cashout"
              {...form.getInputProps('autoCashout', { type: 'checkbox' })}
            />
            {/* Пʼятий тумблер: у мокапі його немає, у таблиці `games` — є. */}
            <Switch
              size="xs"
              label="мультиплеєр"
              data-testid="edit-game-multiplayer"
              {...form.getInputProps('multiplayer', { type: 'checkbox' })}
            />
          </Group>
        </Box>

        <Box>
          <Text className={classes.dim} mb={6}>
            Готовність
          </Text>
          <GameReadinessCheck gameId={row.id} fallback={row.readiness} />
        </Box>

        <Box>
          <Text className={classes.dim} mb={6}>
            Медіа
          </Text>
          <GameMediaSlots gameId={row.id} assets={row.assets} />
        </Box>

        {/*
          * Ключові слова гри.
          *
          * `keywords.owner_type` тримає `game` з першого дня, але побачити ядро гри можна було
          * лише на спільному екрані `/core`, відфільтрувавши його там руками. Компонент той
          * самий, що й у картці бренда: три копії розійшлися б на першій правці, і фільтр,
          * доданий в одну, не зʼявився б у двох інших — кожна при цьому виглядала б робочою.
          */}
        <Box>
          <Text className={classes.dim} mb={6}>
            Ключові слова
          </Text>
          <EntityKeywords ownerType="game" ownerId={row.id} />
        </Box>
      </Stack>
    </RowEditor>
  );
}
